import { useQuery } from '@tanstack/react-query';
import { post } from '@/lib/api/client';
import {
  ALL_ORDERS,
  GROUPING_CODE,
  LOSS_REASON_KEY,
  type CategoriesTrendComparisonDto,
  type CategoriesTrendsDto,
  type ItemsTrendsDto,
  type ItemsTrendsRequest,
  type ItemTrendComparisonDto,
  type LossTrendsDto,
  type SaleTrendsDto,
  type TenderTypesTrendsDto,
  type TenderTypeTrendComparisonDto,
  type TrendsRequest,
} from './dashboard.dto';
import type {
  DashboardData,
  DashboardQuery,
  DashboardSales,
  DashboardStat,
  Grouping,
  GroupingRow,
  LossRow,
  PaymentTypeRow,
  ProductRow,
} from './dashboard.types';

/**
 * The dashboard is a handful of `analytics/v2/trends/*` calls, not one. There
 * is no combined endpoint, so the five that make up the page body go out
 * together and it waits for all of them — a dashboard where three of the five
 * sections show numbers reads as broken rather than as loading.
 *
 * "Where it sold" is deliberately not in that batch. `trends/categories`
 * answers for one grouping at a time and is by far the most expensive of the
 * set — it reads every order line in the range — so asking for all four
 * up front would quadruple the slowest request to fill three tabs nobody has
 * opened. It is its own query per grouping instead, cached once visited.
 *
 * Everything the components need that the API does not send — percent growth,
 * participation shares, a usable product name — is derived here, so the view
 * layer never sees an API field.
 */

/** Top products the API returns; the section shows them all, sorted client-side. */
const TOP_PRODUCT_COUNT = 25;

/** Nothing about the range changes minute to minute, and every call is expensive. */
const STALE_TIME = 5 * 60_000;

/**
 * The API answers with a fraction rounded to two decimals, and uses 1 or 0 to
 * stand in for "the previous period was empty" rather than saying so. That
 * makes a first-ever period look like a flat 100% rise, so the empty case is
 * turned back into "no comparison" and the badge is dropped instead.
 */
function toStat(value: number, previousValue: number, growth: number): DashboardStat {
  return {
    value,
    previousValue,
    change: previousValue > 0 ? growth * 100 : null,
  };
}

/** Participation, which no trends endpoint returns — a row's share of its own table. */
function withShare<T>(rows: T[], valueOf: (row: T) => number): (T & { share: number })[] {
  const total = rows.reduce((sum, row) => sum + valueOf(row), 0);
  return rows.map((row) => ({
    ...row,
    share: total > 0 ? (valueOf(row) / total) * 100 : 0,
  }));
}

type StatTriple = [value: number, previousValue: number, growth: number];

function toSales(source: {
  orders: StatTriple;
  grossSalesCost: StatTriple;
  grossSales: StatTriple;
  netSales: StatTriple;
  averageTicket: StatTriple;
  amountCollected: StatTriple;
  merchandise: StatTriple;
  giftCardRedemptions: StatTriple;
}): DashboardSales {
  return {
    orders: toStat(...source.orders),
    grossSalesCost: toStat(...source.grossSalesCost),
    grossSales: toStat(...source.grossSales),
    netSales: toStat(...source.netSales),
    averageTicket: toStat(...source.averageTicket),
    amountCollected: toStat(...source.amountCollected),
    merchandise: toStat(...source.merchandise),
    giftCardRedemptions: toStat(...source.giftCardRedemptions),
  };
}

function mapPaymentRows(dto: TenderTypesTrendsDto): PaymentTypeRow[] {
  const rows = (dto.comparisons ?? []).map(
    (row: TenderTypeTrendComparisonDto, index: number) => ({
      id: row.name ?? `payment-${index}`,
      name: row.name ?? '',
      orders: toStat(row.currentOrders, row.previousOrders, row.ordersGrowth),
      amountCollected: toStat(
        row.currentAmountCollected,
        row.previousAmountCollected,
        row.amountCollectedGrowth
      ),
      refundTotal: toStat(
        row.currentRefundTotal,
        row.previousRefundTotal,
        row.refundTotalGrowth
      ),
      salesTotal: toStat(
        row.currentSalesTotal,
        row.previousSalesTotal,
        row.salesTotalGrowth
      ),
      averageTicket: toStat(
        row.currentAverageTicket,
        row.previousAverageTicket,
        row.averageTicketGrowth
      ),
    })
  );
  return withShare(rows, (row) => row.amountCollected.value);
}

function mapProductRows(dto: ItemsTrendsDto): ProductRow[] {
  const rows = (dto.comparisons ?? []).map(
    (row: ItemTrendComparisonDto, index: number) => ({
      id: row.itemId ?? `product-${index}`,
      // The API keeps brand, name and description apart; every list in the old
      // portal showed them joined, and a bare name is ambiguous across brands.
      name: [row.brand, row.name, row.description]
        .filter((part) => part && part.trim().length > 0)
        .join(' '),
      upc: row.upc ?? '',
      sold: toStat(row.currentSales, row.previousSales, row.salesGrowth),
      orders: toStat(row.currentOrders, row.previousOrders, row.ordersGrowth),
      salesTotal: toStat(
        row.currentSalesTotal,
        row.previousSalesTotal,
        row.salesTotalGrowth
      ),
      profit: toStat(row.currentProfit, row.previousProfit, row.profitGrowth),
    })
  );
  return withShare(rows, (row) => row.salesTotal.value);
}

function mapLossRows(dto: LossTrendsDto): LossRow[] {
  const rows = (dto.comparisons ?? []).map((row) => ({
    id: String(row.reason),
    reasonKey: LOSS_REASON_KEY[row.reason] ?? 'dashboard.reasonUnknown',
    units: toStat(row.currentTotal, row.previousTotal, row.growth),
  }));
  return withShare(rows, (row) => row.units.value);
}

/**
 * The previous range, which the endpoints report only as a preformatted en-US
 * string. The rule behind it is fixed — the same span shifted back by its own
 * length — so it is recomputed here and stays in the user's locale.
 */
function previousRange(startDate: string, endDate: string) {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000) + 1;
  const shift = (date: Date) => {
    const shifted = new Date(date);
    shifted.setDate(shifted.getDate() - days);
    return shifted.toISOString().slice(0, 10);
  };
  return { previousStartDate: shift(start), previousEndDate: shift(end) };
}

function buildRequest(query: DashboardQuery): TrendsRequest {
  const end = new Date(`${query.endDate}T00:00:00`);
  end.setDate(end.getDate() + 1);
  return {
    StartDate: query.startDate,
    // The endpoints treat EndDate as exclusive — the legacy portal added a day
    // to every range for exactly this reason — so the last day is included here.
    EndDate: end.toISOString().slice(0, 10),
    BusinessIds: query.storeIds,
    // The redesign has no channel filter; every section reports on all orders.
    CompareLastYear: false,
    Type: ALL_ORDERS,
  };
}

/** Stable across reorderings, so the same stores in another order are a cache hit. */
function storeKey(storeIds: string[]) {
  return [...storeIds].sort().join(',');
}

async function fetchDashboard(query: DashboardQuery): Promise<DashboardData> {
  const body = buildRequest(query);

  const [sales, tenderTypes, cardTypes, products, loss] = await Promise.all([
    post<SaleTrendsDto>('analytics/v2/trends/sales', body),
    post<TenderTypesTrendsDto>('analytics/v2/trends/tendertypes', body),
    post<TenderTypesTrendsDto>('analytics/v2/trends/cards', body),
    post<ItemsTrendsDto>('analytics/v2/trends/items', {
      ...body,
      Count: TOP_PRODUCT_COUNT,
      SearchTerm: '',
    }),
    post<LossTrendsDto>('analytics/v2/trends/loss', body),
  ]);

  const series = (sales.comparisons ?? []).map((point) => ({
    date: point.currentStartDate,
    previousDate: point.previousStartDate,
    label: point.currentLabel ?? '',
    previousLabel: point.previousLabel ?? '',
    sales: toSales({
      orders: [point.currentOrders, point.previousOrders, point.ordersGrowth],
      // The per-bucket field transposes the words the period-level one uses.
      grossSalesCost: [
        point.currentGrossSalesCost,
        point.previousGrossSalesCost,
        point.grossSalesGrowthCost,
      ],
      grossSales: [
        point.currentGrossSales,
        point.previousGrossSales,
        point.grossSalesGrowth,
      ],
      netSales: [point.currentNetSales, point.previousNetSales, point.netSalesGrowth],
      averageTicket: [
        point.currentAverageTicket,
        point.previousAverageTicket,
        point.averageTicketGrowth,
      ],
      amountCollected: [
        point.currentAmountCollected,
        point.previousAmountCollected,
        point.amountCollectedGrowth,
      ],
      merchandise: [
        point.currentMerchandise,
        point.previousMerchandise,
        point.merchandiseGrowth,
      ],
      giftCardRedemptions: [
        point.currentGiftCardRedemptions,
        point.previousGiftCardRedemptions,
        point.giftCardRedemptionsGrowth,
      ],
    }),
  }));

  return {
    startDate: query.startDate,
    endDate: query.endDate,
    ...previousRange(query.startDate, query.endDate),

    sales: toSales({
      orders: [sales.currentOrders, sales.previousOrders, sales.ordersGrowth],
      grossSalesCost: [
        sales.currentGrossSalesCost,
        sales.previousGrossSalesCost,
        sales.grossSalesCostGrowth,
      ],
      grossSales: [
        sales.currentGrossSales,
        sales.previousGrossSales,
        sales.grossSalesGrowth,
      ],
      netSales: [sales.currentNetSales, sales.previousNetSales, sales.netSalesGrowth],
      averageTicket: [
        sales.currentAverageTicket,
        sales.previousAverageTicket,
        sales.averageTicketGrowth,
      ],
      amountCollected: [
        sales.currentAmountCollected,
        sales.previousAmountCollected,
        sales.amountCollectedGrowth,
      ],
      merchandise: [
        sales.currentMerchandise,
        sales.previousMerchandise,
        sales.merchandiseGrowth,
      ],
      giftCardRedemptions: [
        sales.currentGiftCardRedemptions,
        sales.previousGiftCardRedemptions,
        sales.giftCardRedemptionsGrowth,
      ],
    }),
    series,

    tenderTypes: mapPaymentRows(tenderTypes),
    cardTypes: mapPaymentRows(cardTypes),

    topProducts: mapProductRows(products),
    loss: mapLossRows(loss),
  };
}

/** Everything on the page except "Where it sold". */
export function useDashboardQuery(query: DashboardQuery) {
  return useQuery({
    queryKey: [
      'dashboard',
      'trends',
      query.startDate,
      query.endDate,
      storeKey(query.storeIds),
    ],
    queryFn: () => fetchDashboard(query),
    staleTime: STALE_TIME,
  });
}

async function fetchGrouping(
  query: DashboardQuery,
  grouping: Grouping,
  spanish: boolean
): Promise<GroupingRow[]> {
  const dto = await post<CategoriesTrendsDto>('analytics/v2/trends/categories', {
    ...buildRequest(query),
    Grouping: GROUPING_CODE[grouping],
    // The per-item drill-down inside every row is the bulk of this response and
    // nothing on this page reads it.
    IncludeItems: false,
  });

  // Department and category carry both translations; division and product group
  // repeat the raw name in each, so picking by language is safe for all four.
  const rows = (dto.comparisons ?? []).map(
    (row: CategoriesTrendComparisonDto, index: number) => ({
      id: row.id ?? `grouping-${index}`,
      name: (spanish ? row.nameSpa : row.nameEng) ?? row.nameEng ?? row.nameSpa ?? '',
      sold: toStat(row.currentSales, row.previousSales, row.salesGrowth),
      orders: toStat(row.currentOrders, row.previousOrders, row.ordersGrowth),
      salesTotal: toStat(
        row.currentSalesTotal,
        row.previousSalesTotal,
        row.salesTotalGrowth
      ),
      profit: toStat(row.currentProfit, row.previousProfit, row.profitGrowth),
    })
  );
  return withShare(rows, (row) => row.salesTotal.value);
}

/** How many rows a page of the top-products detail asks for. */
export const PRODUCT_PAGE_SIZE = 20;

/**
 * The top-products detail list. Unlike every other breakdown on the page this
 * one is not a slice of what the page already holds: the endpoint pages and
 * searches server-side, so a search reaches the whole catalogue instead of
 * filtering the twenty-five rows the summary happened to fetch.
 */
export function useTopProductsPage(
  query: DashboardQuery,
  page: number,
  term: string
) {
  const trimmed = term.trim();
  return useQuery({
    queryKey: [
      'dashboard',
      'products',
      query.startDate,
      query.endDate,
      storeKey(query.storeIds),
      page,
      trimmed,
    ],
    queryFn: async () => {
      const body: ItemsTrendsRequest = {
        ...buildRequest(query),
        Page: page,
        Size: PRODUCT_PAGE_SIZE,
        SearchTerm: trimmed,
      };
      const dto = await post<ItemsTrendsDto>('analytics/v2/trends/items', body);
      return { rows: mapProductRows(dto), total: dto.total ?? 0 };
    },
    // Keeps the previous page on screen while the next one loads, so paging
    // does not blink the list away and back.
    placeholderData: (previous) => previous,
    staleTime: STALE_TIME,
  });
}

/** "Where it sold", one grouping at a time — see the note at the top of the file. */
export function useGroupingQuery(
  query: DashboardQuery,
  grouping: Grouping,
  language: string
) {
  const spanish = language.toLowerCase().startsWith('es');
  return useQuery({
    queryKey: [
      'dashboard',
      'grouping',
      grouping,
      query.startDate,
      query.endDate,
      storeKey(query.storeIds),
      spanish ? 'es' : 'en',
    ],
    queryFn: () => fetchGrouping(query, grouping, spanish),
    staleTime: STALE_TIME,
  });
}
